import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ArticleRating, ArticleRatingResponse } from 'src/app/interfaces/api';
import {
  IStrapiDocument,
  IStrapiDocuments,
  IStudentLife,
  IStudentLifeFilter,
  Page,
  StudentLifeArticleCard,
  StudentLifeFilter
} from 'src/app/interfaces/strapi';
import { ArticleService } from 'src/app/services/article.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import StrapiService from 'src/app/services/strapi.service';
import { ISortByOptions } from 'src/app/shared/components/sort-by-modal/sort-by-modal.component';
import { NgFor, NgIf } from '@angular/common';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  PageRetrievalErrorComponent
} from '../../../shared/components/page-retrieval-error/page-retrieval-error.component';
import { FilterBarComponent } from '../../../shared/components/filter-bar/filter-bar.component';
import { SortByModalComponent } from '../../../shared/components/sort-by-modal/sort-by-modal.component';
import { YoutubeVideoComponent } from '../../../shared/components/youtube-video/youtube-video.component';

@Component({
    selector: 'app-student-life',
    templateUrl: './student-life.component.html',
    styleUrls: ['./student-life.component.scss'],
    changeDetection:ChangeDetectionStrategy.OnPush,
    imports: [
        BreadcrumbsComponent,
        NgIf,
        PageRetrievalErrorComponent,
        FilterBarComponent,
        SortByModalComponent,
        NgFor,
        YoutubeVideoComponent,
    ],
})
export class StudentLifeComponent extends PageComponent implements OnInit {
  public constructor(
    protected override readonly titleService: Title,
    protected override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public readonly sanitizer: DomSanitizer,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    private readonly articleService: ArticleService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  protected override pageData: Page = <Page>{};

  public override pageRetrievalError = false;

  public articleCards: StudentLifeArticleCard[] = [];

  public filteredArticleCards: StudentLifeArticleCard[] = [];

  public filters: StudentLifeFilter[] = [];

  public activeFilter: number | null = null;

  public articleRatings: ArticleRating = {};

  public isMobile: boolean = false;

  ngOnInit(): void {
    this.isMobile = window.innerWidth < 992;
    this.strapi
      .getStudentLifePage()
      .subscribe((studentLife: IStrapiDocument<IStudentLife>) => {
        this.parsePageData(studentLife.data.attributes);
        super.setMetas();
      });

    this.strapi
      .getStudentLifeFilter()
      .subscribe((data: IStrapiDocuments<IStudentLifeFilter>) => {
        this.filters = data.data[0].attributes.filters;
      });

    this.strapi.getStudentLifeArticles().subscribe({
      next: (data: IStrapiDocuments<StudentLifeArticleCard>) => {
        this.articleCards = data.data.map((article) => {
          const studentLifeArticle = article.attributes;
          studentLifeArticle.uid = article.attributes.uid ?? '';
          return studentLifeArticle;
        });

        const articleIds = this.articleCards.map((article) => article.uid);
        this.articleService.getAverageRatings(articleIds).subscribe((res) => {
          const result = <ArticleRatingResponse>res;
          this.articleRatings = result.data;
        });

        this.articleService.filteredArticles = this.articleCards;
        this.filteredArticleCards = this.articleCards;

        if (this.articleService.activeArticleFilter) {
          this.onFilterChange([+this.articleService.activeArticleFilter]);
        }
      },
      complete: () => {
        this.checkForTagParams();
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * check and filter by url parameters if they exist
   */
  private checkForTagParams(): void {
    // Get the tag query param if we navigate and want to filter
    const tags = this.activeRoute.snapshot.queryParamMap.get('tag');

    if (tags) {
      this.onFilterChange(tags.split(',').map((tag) => Number(tag)));
    }
  }

  public checkHasRating(articleUid: string): boolean {
    if (!this.articleRatings[articleUid]) {
      return true;
    }
    return this.articleRatings[articleUid] < 3;
  }

  /**
   * @param selectedFilters - array of filters to filter articles by
   * @returns
   */
  public onFilterChange(selectedFilters: number[]): void {
    if (selectedFilters.includes(-1)) {
      this.filteredArticleCards = this.articleCards;
      return;
    }

    // filter articles where some of the tags we want to filter by
    // are in the articles we are checking
    this.filteredArticleCards = this.articleCards.filter(
      (article: StudentLifeArticleCard) =>
        selectedFilters.some((tag) => {
          const result = article.tags.split(',').map(Number).includes(tag);
          return result;
        })
    );
  }

  public sortArticlesBy(sortCategory: ISortByOptions) {
    switch (sortCategory) {
      case 'alphabetical':
        this.filteredArticleCards = this.filteredArticleCards.sort(
          (current: StudentLifeArticleCard, next: StudentLifeArticleCard) =>
            current.title.localeCompare(next.title)
        );
        break;
      case 'publish-date':
        this.filteredArticleCards = this.filteredArticleCards.sort(
          (current, next) => {
            const dateCurrent = new Date(current.date_published);
            const dateNext = new Date(next.date_published);
            return dateNext.getTime() - dateCurrent.getTime();
          }
        );
        break;
      case 'rating':
        this.filteredArticleCards = this.filteredArticleCards.sort(
          (current, next) => {
            const currentArticle = this.articleRatings[current.uid] ?? 0;
            const nextArticle = this.articleRatings[next.uid] ?? 0;
            return nextArticle - currentArticle;
          }
        );
        break;
      default:
        break;
    }
  }

  public onOpenArticle(articleUid: string): void {
    this.router
      .navigate([`app/student-life/${articleUid}`])
      .finally(() => false);
  }

  public openYoutubeVideo(video_id: string) {
    window.open(`https://www.youtube.com/watch?v=${video_id}`);
  }

  public processExcerpt(excerpt: string): string {
    return `${excerpt.split(' ', 12).join(' ')} ...`;
  }
}
